import React, { createContext, useContext, useMemo } from 'react';
import { useSiteWorkspace } from './SiteWorkspaceContext';
import type { RestaurantBrand } from '../types/restaurant';
import type { RestaurantSettings } from '../types/workspace';
import {
  formatPrice,
  getButtonRadiusClass,
  getShellBackground,
  hexToRgb,
} from '../utils/restaurantBranding';

interface RestaurantContextType {
  restaurant: RestaurantBrand;
  settings: RestaurantSettings;
  phoneNumber: string;
  hasCallablePhoneNumber: boolean;
  callHref?: string;
  mapsHref: string;
  formatMoney: (price: number) => string;
  theme: {
    cssVars: React.CSSProperties;
    shellBackground: string;
    buttonRadius: string;
  };
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

const isCallablePhoneNumber = (value: string) => /^[+]?[\d\s()-]{6,}$/.test(value.trim());
const settingsToBrand = (settings: RestaurantSettings): RestaurantBrand => ({
  name: settings.name,
  subtitle: settings.subtitle,
  location: settings.location,
  phoneNumber: settings.phoneNumber,
  coverImage: settings.coverImage,
  menuUrl: settings.menuUrl,
  logoImage: settings.logoImage,
  faviconImage: settings.faviconImage,
  callToActionLabel: settings.callToActionLabel,
  mapsUrl: settings.mapsUrl,
  currencyCode: settings.currencyCode,
  currencySymbol: settings.currencySymbol,
});

export const RestaurantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useSiteWorkspace();
  const phoneNumber = settings.phoneNumber.trim();
  const hasCallablePhoneNumber = isCallablePhoneNumber(phoneNumber);
  const primaryRgb = hexToRgb(settings.brandColors.primary);
  const accentRgb = hexToRgb(settings.brandColors.accent);
  const shellBackground = getShellBackground(
    settings.backgroundStyle,
    settings.brandColors,
    settings.coverImage,
  );
  const buttonRadius = getButtonRadiusClass(settings.buttonStyle);

  const value = useMemo<RestaurantContextType>(
    () => ({
      restaurant: settingsToBrand(settings),
      settings,
      phoneNumber,
      hasCallablePhoneNumber,
      callHref: hasCallablePhoneNumber ? `tel:${phoneNumber}` : undefined,
      mapsHref: settings.mapsUrl,
      formatMoney: (price) => formatPrice(price, settings),
      theme: {
        shellBackground,
        buttonRadius,
        cssVars: {
          '--brand-primary': settings.brandColors.primary,
          '--brand-accent': settings.brandColors.accent,
          '--brand-surface': settings.brandColors.surface,
          '--brand-background': settings.brandColors.background,
          '--brand-text': settings.brandColors.text,
          '--brand-shell-background': shellBackground,
          '--brand-primary-rgb': `${primaryRgb.r} ${primaryRgb.g} ${primaryRgb.b}`,
          '--brand-accent-rgb': `${accentRgb.r} ${accentRgb.g} ${accentRgb.b}`,
          '--brand-button-radius': buttonRadius,
        } as React.CSSProperties,
      },
    }),
    [buttonRadius, hasCallablePhoneNumber, phoneNumber, primaryRgb.b, primaryRgb.g, primaryRgb.r, settings, shellBackground, accentRgb.r, accentRgb.g, accentRgb.b],
  );

  return <RestaurantContext.Provider value={value}>{children}</RestaurantContext.Provider>;
};

export const useRestaurant = (): RestaurantContextType => {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};

export default RestaurantContext;
